export type SourceTool = 'velociraptor' | 'zimmerman' | 'volatility' | 'redline' | 'encase'

export type IngestionRunStatus =
  | 'pending'
  | 'extracting'
  | 'parsing'
  | 'verifying'
  | 'completed'
  | 'completed_with_issues'
  | 'failed'

export const ACTIVE_INGESTION_STATUSES: ReadonlySet<IngestionRunStatus> = new Set([
  'pending',
  'extracting',
  'parsing',
  'verifying',
])

/**
 * True while a run is still in progress. Terminal statuses
 * (completed, completed_with_issues, failed) and any unrecognised value are
 * treated as NOT active — fail-safe so pollers and disabled-state guards
 * always settle rather than run forever.
 */
export function isActiveIngestionStatus(status: string): boolean {
  return (ACTIVE_INGESTION_STATUSES as ReadonlySet<string>).has(status)
}

export type HostnameMatchStatus = 'matched' | 'mismatched' | 'unchecked'
export type IssueSeverity = 'error' | 'warning' | 'info'
export type IssueType =
  | 'malformed_row'
  | 'missing_field'
  | 'type_mismatch'
  | 'structural'
  | 'missing_artefact'
  | 'empty_artefact'

export interface ArtefactSummaryEntry {
  artefactType: string
  fileName: string
  expectedRows: number | null
  parsedRows: number
  skippedRows: number
  collectionParameters?: Record<string, string | null> | null
  status: 'completed' | 'failed' | 'empty' | 'missing'
}

export interface IngestionRun {
  id: string
  caseId: string
  collectionReceiptId: string
  evidenceNumberId: string
  endpointId: string
  status: IngestionRunStatus
  triggeredBy: string
  triggeredAt: string
  sourcePath: string
  sourceHashSha256: string | null
  clientHostname: string | null
  clientOs: string | null
  clientFqdn: string | null
  clientArchitecture: string | null
  clientTimezone: string | null
  hostnameMatchStatus: HostnameMatchStatus
  collectionSessionId: string | null
  collectionDurationSecs: number | null
  totalExpectedRows: number | null
  totalParsedRows: number
  totalSkippedRows: number
  artefactSummary: ArtefactSummaryEntry[]
  sourceTool: SourceTool | null
  sourceToolVersion: string | null
  detectionMethod: 'auto' | 'manual' | null
  isVerified: boolean
  verifiedAt: string | null
  verifiedBy: string | null
  isCorrupt: boolean
  flaggedCorruptAt: string | null
  flaggedCorruptBy: string | null
  corruptReason: string | null
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Response body for the two ingestion-run creation endpoints (full and
 * selective run start): the existing `IngestionRun` shape plus the shared
 * progress facility's `operationId`, mirroring the backend's flattened
 * `IngestionRunStartOutput` (evidence-container-foundation PRP addendum).
 * `null` means progress-row creation itself failed server-side (best-effort
 * write) -- the run still started, so callers must treat `null` as "no
 * inline progress available", never as an error. `GET`/`verify`/
 * `flag-corrupt` responses are unaffected and keep using `IngestionRun`.
 */
export interface IngestionRunStart extends IngestionRun {
  operationId: string | null
}

export interface IngestionIssue {
  id: string
  ingestionRunId: string
  artefactType: string
  issueType: IssueType
  severity: IssueSeverity
  lineNumber: number | null
  detail: string
  rawData: string | null
  createdAt: string
}

export interface IssueCounts {
  error: number
  warning: number
  info: number
}

export interface IngestionIssuesResponse {
  issues: IngestionIssue[]
  counts: IssueCounts
}

export interface ProcessableCollection {
  receiptId: string
  evidenceNumber: string
  endpointHostname: string
  endpointId: string
  receivedAt: string
  packageFilename: string
  ingestionStatus: string
  lastRunStatus: string | null
  canProcess: boolean
}

export const INGESTION_RUN_STATUS_LABELS: Record<IngestionRunStatus, string> = {
  pending: 'Pending',
  extracting: 'Extracting',
  parsing: 'Parsing',
  verifying: 'Verifying',
  completed: 'Completed',
  completed_with_issues: 'Completed with Issues',
  failed: 'Failed',
}

export const INGESTION_RUN_STATUS_COLOURS: Record<IngestionRunStatus, string> = {
  pending: 'gray',
  extracting: 'blue',
  parsing: 'blue',
  verifying: 'amber',
  completed: 'green',
  completed_with_issues: 'amber',
  failed: 'red',
}

export const ISSUE_SEVERITY_COLOURS: Record<IssueSeverity, string> = {
  error: 'red',
  warning: 'amber',
  info: 'blue',
}

export interface IngestionArtefactFile {
  id: string
  ingestionRunId: string
  artefactName: string
  sourceTool: SourceTool
  relativePath: string
  ingestSha256: string
  sourceFileSha256: string | null
  sourceFileMd5: string | null
  sourceFileSizeBytes: number | null
  capturedRowCount: number | null
  createdAt: string
}

export interface IngestionFieldMapping {
  id: string
  ingestionRunId: string
  sourceTool: SourceTool
  artefactType: string
  mapping: Record<string, string>
  droppedFields: string[]
  createdAt: string
}

/**
 * Input for `POST /cases/:id/ingestion-runs`.
 *
 * `selectedItemKeys`/`reingestItemKeys` are evidence-container-foundation PRP
 * additions (FR8-FR12): both absent ⇒ a full run, preserving backward
 * compatibility for every existing caller (PRP §4, AC8). Omitting a field
 * here (rather than passing `undefined` explicitly) keeps `JSON.stringify`
 * from emitting the key at all, so a full-run request body is byte-identical
 * to the pre-PRP shape.
 */
export interface StartIngestionInput {
  collectionReceiptId: string
  sourceTool?: SourceTool
  sourceFileSha256?: string
  selectedItemKeys?: string[] | undefined
  reingestItemKeys?: string[] | undefined
}
